import React, {useMemo, useState} from 'react';
import ControlledList from '@src/components/ControlledList/ControlledList';
import app from '@src/helpers/app';
import {IModelFileToImportModel} from '@src/@interfaces/models/ModelFileToImport/IModelFileToImportModel';
import {ListConfigFieldTypes, ListConfigNamedColumn} from '@src/@types/components/List';
import {useQuery} from '@tanstack/react-query';
import useListContentPropsForAdditionalRow from '@src/components/ControlledList/useListContentPropsForAdditionalRow';
import StatusColumn from '@src/components/_views/configurable/import-view/file-list/columns/StatusColumn';
import useWebSocket from '@src/hooks/useWebSocket';
import {TBaseWSMessage} from '@src/@types/hooks/useWebSocket';
import {singular} from 'pluralize';
import useDebounce from '@src/hooks/useDebounce';
import Translate from '@src/components/Translate/Translate';
import AdditionalRow from '@src/components/_views/configurable/import-view/file-list/additional-row/AdditionalRow';
import {useLocation, useNavigate} from 'react-router-dom';
import classnames from '@src/helpers/classnames';
import {IImportConfig} from '@src/@interfaces/components/Import/IImportConfig';

const columns: ListConfigNamedColumn[] = [
    {
        name: 'id',
        label: '#',
        type: ListConfigFieldTypes.NUMBER,
        invisible: true,
    },
    {
        name: 'file_public_name',
        label: 'List:ImportRecords.list.fields.file_public_name',
        type: ListConfigFieldTypes.TEXT,
    },
    {
        name: 'status',
        label: 'List:ImportRecords.list.fields.status',
        type: ListConfigFieldTypes.SELECTABLE,
    },
    {
        name: 'created_at',
        label: 'List:ImportRecords.list.fields.created_at',
        type: ListConfigFieldTypes.TIME_SINCE,
    },
    {
        name: 'completed_at',
        label: 'List:ImportRecords.list.fields.completed_at',
        type: ListConfigFieldTypes.TIME_SINCE,
    },
    {
        name: 'attempts_number',
        label: 'List:ImportRecords.list.fields.attempts_number',
        type: ListConfigFieldTypes.NUMBER,
        invisible: true,
    },
    {
        name: 'last_attempt_at',
        label: 'List:ImportRecords.list.fields.last_attempt_at',
        type: ListConfigFieldTypes.TIME_SINCE,
        invisible: true,
    },
];

const statusesToDisplaySettingsRow: IModelFileToImportModel['status'][] = [
    'waiting-for-settings',
    'waiting-for-start',
    'idle',
];

const statusesToDisplayDeleteButton: IModelFileToImportModel['status'][] = [
    'waiting-for-temp-file',
    'waiting-for-settings',
    'waiting-for-start',
    'enqueued',
    'idle',
];

const statusesToDisplayStartButton: IModelFileToImportModel['status'][] = [
    'waiting-for-start',
    'idle',
];

export default function List(props: {
    controllerName: string,
    config: IImportConfig,
}) {
    const {controllerName, config} = props;
    const wrappedControllerName = `ModelFileToImport:${controllerName}`;
    const {listNames: nameResolver} = app('names-resolver');

    const location = useLocation();
    const navigate = useNavigate();
    const selectedFileHash = location.hash.replace(/^#+/guim, '');
    const [deletedItems, setDeletedItems] = useState<Record<string | number, number>>({});

    const {lastEventFiredAt} = useWebSocket<TBaseWSMessage<'ModelFileToImport'>>({
        model: singular(controllerName),
        event: '*',
        filterMessages: (event, payload) => {
            return payload.model === 'ModelFileToImport';
        },
    });
    const debouncedLastEventFiredAt = useDebounce(lastEventFiredAt, 1000);
    const queryKey = app('queryKeys').getListOfExactModelRecordsQueryKey(
            wrappedControllerName,
            undefined,
            {lastEventFiredAt: debouncedLastEventFiredAt},
    );
    const queryResult = useQuery<IModelFileToImportModel[], Error>(
            queryKey,
            () => app('http').route('abstract-models.import.index', {}, {
                json: {'controller-name': controllerName},
            }),
            {keepPreviousData: true},
    );

    const contentProps = useListContentPropsForAdditionalRow<IModelFileToImportModel, {
        controllerName: string,
        config: IImportConfig,
    }>({
        additionalRow: {
            additionalProps: {
                controllerName,
                config,
            },
            forceExpanded: record => {
                return (
                        record != null &&
                        statusesToDisplaySettingsRow.includes(record.status) &&
                        record.temp_file_hash === selectedFileHash
                );
            },
            additionalRowComponent: AdditionalRow,
            customColumnsComponents: {
                status: StatusColumn,
            }
        },
        removeRow: {
            display: record => statusesToDisplayDeleteButton.includes(record.status),
            disabled: record => !statusesToDisplayDeleteButton.includes(record.status),
            onClick: (i, record) => {
                const id = record?.id;
                if (id == null) {
                    return;
                }

                return app('http').route('abstract-models.import.delete', {
                    id: String(id),
                }, {
                    json: {'controller-name': controllerName},
                }).then(v => {
                    setDeletedItems(map => ({...map, [id]: Date.now()}));

                    return v;
                });
            },
        },
        additionalRowControls: [
            // start
            {
                display: record => statusesToDisplayStartButton.includes(record.status),
                disabled: record => !statusesToDisplayStartButton.includes(record.status),
                label: <i className="fa fa-play"/>,
                className: 'btn btn-sm btn-success',
                onClick(record) {
                    return app('http').route('abstract-models.import.start-import', {
                        id: String(record.id),
                    }, {
                        json: {'controller-name': controllerName},
                    });
                },
            },

            //stop
            {
                display: record => record.status === 'enqueued',
                disabled: record => record.status !== 'enqueued',
                label: <i className="fa fa-pause"/>,
                className: 'btn btn-sm btn-warning',
                onClick(record) {
                    return app('http').route('abstract-models.import.stop-import', {
                        id: String(record.id),
                    }, {
                        json: {'controller-name': controllerName},
                    });
                },
            },

            // settings
            {
                display: record => statusesToDisplaySettingsRow.includes(record.status),
                disabled: record => !statusesToDisplaySettingsRow.includes(record.status),
                label: <>
                    <i className="fa fa-cogs me-2"/>
                    <Translate message="List:ImportRecords.list.configure-import-file-button-label"/>
                </>,
                className: record => classnames('btn btn-sm', {
                    'btn-outline-info': record.temp_file_hash !== selectedFileHash,
                    'btn-outline-secondary': record.temp_file_hash === selectedFileHash,
                }),
                onClick(record) {
                    if (selectedFileHash === record.temp_file_hash) {
                        navigate({...location, hash: undefined});
                    } else {
                        navigate({...location, hash: record.temp_file_hash});
                    }
                },
            }
        ],
    });

    const records = useMemo<IModelFileToImportModel[]>(() => {
        return (queryResult?.data ?? []).filter(item => item.id != null && !(item.id in deletedItems));
    }, [deletedItems, queryResult?.data]);

    return <ControlledList
            uniqueListName={nameResolver.getUniqueName(wrappedControllerName)}
            controllerName={wrappedControllerName}
            records={records}
            queryResult={queryResult}
            columns={columns}
            listContentProps={contentProps}
            withoutSearchField
    />;
}
